"use client";

import React, { useState } from "react";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { Button } from "@/components/ui/button";
import { getImagePath } from "@/lib/utils";
import { useTranslations } from "next-intl";

// Example rental fee only — the owner sets their own rent.
const DEFAULT_RENT = 50;

const Page = () => {
  const t = useTranslations("partnercalculator");

  const [sticks, setSticks] = useState<number | "">("");
  const [rentalsPerWeek, setRentalsPerWeek] = useState<number | "">("");
  const [weeks, setWeeks] = useState<number | "">("");
  const [rent, setRent] = useState<number | "">(DEFAULT_RENT);
  const [showResult, setShowResult] = useState(false);

  const toInt = (v: number | "") => (typeof v === "number" ? v : 0);

  // Gross device-rental receipts = sticks × rentals per stick per week × weeks × owner-selected rent.
  // The ₹99 movie payment goes to KST and is not part of this.
  const receipts = toInt(sticks) * toInt(rentalsPerWeek) * toInt(weeks) * toInt(rent);

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!toInt(sticks) || !toInt(rentalsPerWeek) || !toInt(weeks) || !toInt(rent)) return;
    setShowResult(true);
  };

  const handleReset = () => {
    setShowResult(false);
  };

  const fields: {
    label: string;
    placeholder: string;
    value: number | "";
    set: (v: number | "") => void;
  }[] = [
    { label: t("label_sticks"), placeholder: t("placeholder_sticks"), value: sticks, set: setSticks },
    { label: t("label_rentals"), placeholder: t("placeholder_rentals"), value: rentalsPerWeek, set: setRentalsPerWeek },
    { label: t("label_weeks"), placeholder: t("placeholder_weeks"), value: weeks, set: setWeeks },
    { label: t("label_rent"), placeholder: t("placeholder_rent"), value: rent, set: setRent },
  ];

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={t("page_title")}
        description=""
        bgImages={[getImagePath("/images/breadcrumb/joinPartner.webp")]}
        bgIndex={0}
        buttons={[
          {
            label: t("buy_stick"),
            href: "/buynow-customer",
            props: {
              variant: "default",
              className:
                "ml-2 rounded-tl-xl rounded-br-xl bg-white text-white border border-white hover:bg-primary hover:text-white bg-gradient-to-r from-[#A23465] to-[#EA7F75]",
            },
          },
        ]}
      />

      <div className="container mx-auto px-2 sm:px-4 pt-6 sm:pt-10">
        <h3 className="text-center text-2xl sm:text-3xl lg:text-4xl text-[#A23465]">
          {t("heading")}
        </h3>

        <p className="max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto mt-5 px-4 py-3 border-l-4 border-[#A23465] bg-[#A23465]/10 text-[#A23465] font-semibold text-sm sm:text-base">
          {t("disclaimer")}
        </p>

        <form
          className="space-y-4 px-3 sm:px-8 md:px-12 lg:px-20 py-8 sm:py-12 md:py-16 lg:py-20 mt-5 max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto bg-PartnerCalulator rounded-tl-[30px] rounded-br-[30px]"
          onSubmit={handleCalculate}
        >
          {!showResult &&
            fields.map((f) => (
              <div key={f.label} className="w-full mb-3">
                <label className="block mb-1 text-white text-sm sm:text-base">
                  {f.label}
                </label>
                <input
                  type="number"
                  min={1}
                  required
                  value={f.value}
                  onChange={(e) =>
                    f.set(
                      e.target.value === ""
                        ? ""
                        : Math.max(1, Number(e.target.value))
                    )
                  }
                  placeholder={f.placeholder}
                  className="w-full p-2 sm:p-3 bg-black/70 text-white placeholder:text-gray-300 rounded text-sm sm:text-base"
                />
              </div>
            ))}

          {showResult && (
            <div className="text-center text-white py-8 sm:py-12 md:py-16 px-3 sm:px-6 md:px-10">
              <p className="text-white">{t("result_title")}</p>

              <strong className="block mt-3 text-2xl sm:text-3xl md:text-5xl">
                ₹ {receipts.toLocaleString("en-IN")}
              </strong>

              <p className="mt-6 text-white/90 text-sm sm:text-base">
                {t("movie_fee_note")}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="border border-white text-white rounded-tl-xl rounded-br-xl px-6 py-2 hover:bg-[#922b57]"
                >
                  {t("reset")}
                </Button>

                <Button
                  asChild
                  className="bg-white text-[#A23465] rounded-tl-xl rounded-br-xl px-6 py-2 hover:bg-white/90"
                >
                  <a href="/buynow-customer">{t("buy_stick")}</a>
                </Button>
              </div>
            </div>
          )}

          {!showResult && (
            <div className="text-center mt-6">
              <Button
                type="submit"
                className="border border-white text-white rounded-tl-xl rounded-br-xl px-4 sm:px-6 py-2 hover:bg-[#922b57]"
              >
                {t("calculate")}
              </Button>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default Page;
